/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the command palette with its grouped, fuzzy-filtered actions.
 * @state-owner palette-local React state; TanStack Query owns the live search request state
 * @allowed-imports React; workspace and dataset stores; shared selectors and hooks; role provider;
 *   module public APIs (incl. modules/search's useTeacherWorkspaceSearch)
 * @forbidden in-product role switching; direct API client
 * @public-api WorkspaceShell
 * @side-effects navigation, list collapse, and a debounced authenticated GET search request per
 *   typed query (via useTeacherWorkspaceSearch), rendering its loading/error/empty states
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts and
 *   frontend/apps/web/e2e/act-f13.spec.ts, CommandPalette.test.tsx
 */

import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useActiveOrganizationId, useActiveRole } from '../../app/providers/ActiveRoleProvider';
import type { SearchEntityType } from '../../modules/search';
import { useTeacherWorkspaceSearch } from '../../modules/search';
import { ACTIVITY_STATUS_LABEL } from '../../shared/lib/demo-data';
import type { WorkspaceSection } from '../../shared/lib/model';
import { fuzzyMatch, myClassIndexes } from '../../shared/lib/selectors';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useDebouncedValue, useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';

interface PaletteItem {
  readonly group: string;
  readonly icon: string;
  readonly title: string;
  readonly detail?: string;
  readonly shortcut?: string;
  /** Already filtered server-side; skip the local fuzzy match so a lagging debounce or a
   *  detail field the query does not literally contain cannot hide a fresh result. */
  readonly serverFiltered?: boolean;
  readonly run: () => void;
}

const KEY_HINT: Readonly<Record<string, string>> = {
  act: 'G A',
  room: 'G C',
  live: 'G L',
  rep: 'G R',
};

const ALL_TEACHER_SEARCH_TYPES: readonly SearchEntityType[] = [
  'classroom',
  'curriculum',
  'activity',
  'student',
];

export function CommandPalette({
  onClose,
  onNavigateSection,
  open,
}: {
  onClose(): void;
  onNavigateSection(section: WorkspaceSection): void;
  open: boolean;
}) {
  const t = useTranslator();
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const toggleList = useWorkspaceUiStore((state) => state.toggleList);
  // The search endpoint requires a non-blank q, so an empty palette query keeps browsing the
  // locally hydrated dataset store (unchanged); only a typed query switches to the live,
  // debounced cross-domain search -- matching SearchOverlay's data-source split.
  const debouncedQuery = useDebouncedValue(query.trim(), 250);
  const organizationId = useActiveOrganizationId();
  const searchQuery = useTeacherWorkspaceSearch(
    teacher ? organizationId : null,
    teacher ? debouncedQuery : '',
    teacher ? ALL_TEACHER_SEARCH_TYPES : [],
  );
  const usingLiveSearch = teacher && Boolean(debouncedQuery);
  const liveSearchLoading = usingLiveSearch && searchQuery.isLoading;
  const liveSearchErrored = usingLiveSearch && searchQuery.isError;

  const items = useMemo<readonly PaletteItem[]>(() => {
    const list: PaletteItem[] = [];
    const navigation: readonly (readonly [string, WorkspaceSection])[] = teacher
      ? [
          ['活動', 'act'],
          ['班級', 'room'],
          ['即時', 'live'],
          ['報告', 'rep'],
        ]
      : [
          ['我的班級', 'room'],
          ['我的作業', 'act'],
          ['我的報告', 'rep'],
        ];
    for (const [label, section] of navigation) {
      list.push({
        group: t('前往'),
        icon: '→',
        title: t(label),
        shortcut: KEY_HINT[section],
        run: () => onNavigateSection(section),
      });
    }
    if (teacher) {
      list.push({
        group: t('動作'),
        icon: '⇥',
        title: t('收合清單'),
        shortcut: '⌘ \\',
        run: toggleList,
      });
    }
    if (usingLiveSearch) {
      const results = searchQuery.data;
      if (results) {
        for (const row of results.classroom) {
          list.push({
            group: t('班級'),
            icon: t('班字'),
            title: row.title,
            detail: row.detail,
            serverFiltered: true,
            run: () => navigate('room', row.id),
          });
        }
        for (const row of results.curriculum) {
          list.push({
            group: t('教材'),
            icon: t('材字'),
            title: row.title,
            detail: row.detail,
            serverFiltered: true,
            run: () => navigate('room', row.id),
          });
        }
        for (const row of results.activity) {
          list.push({
            group: t('活動'),
            icon: t('題字'),
            title: row.title,
            detail: row.detail,
            serverFiltered: true,
            run: () => navigate('act', row.id),
          });
        }
        for (const row of results.student) {
          list.push({
            group: t('學生'),
            icon: row.title[0],
            title: row.title,
            detail: row.detail,
            serverFiltered: true,
            run: () => navigate('rep', row.id),
          });
        }
      }
    } else {
      const classIndexes = teacher
        ? classes.map((_, position) => position)
        : myClassIndexes(classes);
      for (const classIndex of classIndexes) {
        const classroom = classes[classIndex];
        list.push({
          group: t('班級'),
          icon: t('班字'),
          title: classroom.name,
          detail: `${classroom.students.length} ${t('人')}`,
          run: () => navigate('room', `c${classIndex}`),
        });
      }
      if (teacher) {
        for (const activity of activities) {
          list.push({
            group: t('活動'),
            icon: t('題字'),
            title: activity.name,
            detail: `${activity.questionCount} ${t('題')} · ${t(
              ACTIVITY_STATUS_LABEL[activity.status] ?? activity.status,
            )}`,
            run: () => navigate('act', activity.id),
          });
        }
        for (const classroom of classes) {
          for (const student of classroom.students) {
            list.push({
              group: t('學生'),
              icon: student[0],
              title: student,
              detail: classroom.name,
              run: () => navigate('rep', student),
            });
          }
        }
      }
    }
    return list;
  }, [
    activities,
    classes,
    navigate,
    onNavigateSection,
    searchQuery.data,
    t,
    teacher,
    toggleList,
    usingLiveSearch,
  ]);

  const visible = useMemo(
    () =>
      items.filter(
        (item) =>
          item.serverFiltered ||
          fuzzyMatch(query, `${item.title} ${item.detail ?? ''} ${item.group}`),
      ),
    [items, query],
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setIndex(0);
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector('.cmdr.on')?.scrollIntoView({ block: 'nearest' });
  }, []);

  if (!open) return null;

  const runSelected = () => {
    const item = visible[index];
    onClose();
    if (item) window.setTimeout(item.run, 0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (visible.length) setIndex((current) => (current + 1) % visible.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (visible.length) setIndex((current) => (current - 1 + visible.length) % visible.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runSelected();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  let lastGroup = '';
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the scrim is a shortcut; Escape and the palette controls are the accessible paths
    <div
      className="cmdov"
      id="cmdov"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="cmdbox">
        <input
          id="cmdq"
          onChange={(event) => {
            setQuery(event.target.value);
            setIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={`${t('輸入指令或搜尋')}…`}
          ref={inputRef}
          value={query}
        />
        <div className="cmdl" id="cmdl" ref={listRef}>
          {liveSearchLoading ? (
            <div className="cmdempty">{t('搜尋中…')}</div>
          ) : liveSearchErrored ? (
            <div className="cmdempty">
              <p>{t('搜尋時發生問題')}</p>
              <button className="bt pri" type="button" onClick={() => void searchQuery.refetch()}>
                {t('重試')}
              </button>
            </div>
          ) : visible.length ? (
            visible.map((item, position) => {
              const heading = item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <div key={`${item.group}-${item.title}-${item.detail ?? ''}`}>
                  {heading ? <div className="cmdg">{heading}</div> : null}
                  <button
                    className={`cmdr ${position === index ? 'on' : ''}`}
                    onMouseMove={() => setIndex(position)}
                    type="button"
                    onClick={runSelected}
                  >
                    <span className="cmdi">{item.icon}</span>
                    <span className="cmdt">
                      {item.title}
                      {item.detail ? <em>{item.detail}</em> : null}
                    </span>
                    {item.shortcut ? (
                      <span className="cmdk">
                        {item.shortcut.split(' ').map((key) => (
                          <kbd key={key}>{key}</kbd>
                        ))}
                      </span>
                    ) : null}
                  </button>
                </div>
              );
            })
          ) : (
            <div className="cmdempty">
              {query.trim() ? `找不到「${query.trim()}」` : t('找不到符合的')}
            </div>
          )}
        </div>
        <div className="cmdf">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t('移動')}
          </span>
          <span>
            <kbd>↵</kbd> {t('執行')}
          </span>
          <span>
            <kbd>esc</kbd> {t('關閉')}
          </span>
        </div>
      </div>
    </div>
  );
}
